import { Estate, EstateJSON } from "../models/Estate";
import { callDelete, callGet, callPost, callPostWithFormData, callPut, laravelApiV1 } from "./index";
import { EstateSummary } from "../models/EstateSummary";
import { CognitoAuth } from "./CognitoAuth";
import { Asset, AssetJSON } from "../models/Asset";
import { AssetImage } from "../models/AssetImage";
import { ProbateType } from "models/ProbateType";
import { isLead } from "../constants"
import { User } from "models/User";
import { UserProfile } from "models/UserProfile";

export type AssetImageUploadResult = {
  asset_id?: number;
  file_name?: string;
  url?: string;
};

export class AtticusEstate {
  async fetchEstates(includeDeleted = false): Promise<Estate[] | undefined> {
    try {
      let path = Estate.path;
      if (includeDeleted) path += "&deleted=true";
      const response = await callGet(path);
      return response?.data?.map((json: any) => {
        const estate = Estate.fromJSON(json)
        if (json.probate_type) {
          estate.probateType = ProbateType.fromJSON(json.probate_type)
        }
        return estate
      });
    } catch (error) {
      !isLead && CognitoAuth.handleError(error, "fetchEstates error");
    }
  }

  async fetchUsersInEstate(estateId: number): Promise<User[] | undefined> {
    try {
      const path = Estate.path + `/${estateId}/users`
      const response = await callGet(path);
      return response?.data?.map((json: any) => {
        const u = User.fromJSON(json)
        u.profile = UserProfile.fromJSON(json.profile || {})
        return u
      });
    } catch (error) {
      !isLead && CognitoAuth.handleError(error, "fetchUsersInEstate error");
    }
  }

  async addUserToEstate(estateId: number, userId: number): Promise<boolean> {
    try {
      await callPost(Estate.path + "/" + estateId + "/users", {
        user_id: userId,
        estate_id: estateId,
      });
      return true;
    } catch (error) {
      console.error(
        (error as any)?.response?.statusText || "addUserToEstate error"
      );
      throw error;
    }
  }

  async fetchSingleEstate(estateId: number): Promise<Estate | undefined> {
    try {
      const response = await callGet(
        Estate.path + "/" + estateId,
        true
      );
      const estate = Estate.fromJSON(response?.data)
      if (response?.data?.probate_type) {
        estate.probateType = ProbateType.fromJSON(response.data.probate_type)
      }
      return estate
    } catch (error) {
      !isLead && CognitoAuth.handleError(error, "fetchSingleEstate error");
    }
    return undefined;
  }

  async createEstate(initialFields: EstateJSON): Promise<number> {
    try {
      const response = await callPost(Estate.path, initialFields);
      return response?.data?.id;
    } catch (error) {
      !isLead && CognitoAuth.handleError(error, "createEstate error");
      throw error;
    }
  }

  async updateEstate(id: number, updateFields: EstateJSON): Promise<boolean> {
    try {
      await callPut(Estate.path + "/" + id, {
        ...updateFields,
        has_will: updateFields.has_will === "1" ? "Yes" :
          updateFields.has_will === "0" ? "No" : updateFields.has_will,
      });
      return true;
    } catch (error) {
      console.error(
        (error as any)?.response?.statusText || "updateEstate error"
      );
      throw error;
    }
  }



  async deleteEstate(id: number): Promise<boolean> {
    try {
      await callDelete(Estate.path + "/" + id);
      return true;
    } catch (error) {
      !isLead && CognitoAuth.handleError(error, "deleteEstate error");
      throw error;
    }
  }

  async getSummary(
    estateId: number | string
  ): Promise<EstateSummary | undefined> {
    try {
      const response = await callGet(
        `${Estate.path}/${estateId}/summary`
      );
      if (response?.data) {
        return EstateSummary.fromJSON(response.data);
      }
    } catch (error) {
      !isLead && CognitoAuth.handleError(error, "getSummary error");
    }
    return undefined;
  }

  async getAssets(
    estateId?: number,
    excludeFI = false
  ): Promise<Asset[] | undefined> {
    try {
      const response = await callGet(
        `${Estate.path}/${estateId || "first"}/assets${excludeFI ? "?nofi=true" : ""}`
      );
      return response?.data?.data?.assets?.map((json: AssetJSON) =>
        Asset.fromJSON(json)
      );
    } catch (error) {
      !isLead && CognitoAuth.handleError(error, "getAssets error");
    }
    return undefined;
  }

  async saveAsset(
    asset?: Asset,
    estateId?: number
  ): Promise<number | undefined> {
    try {
      const url = `${Estate.path}/${estateId || "first"}/assets${asset?.id ? "/" + asset?.id : ""
        }`;
      let response;
      if (asset?.id) {
        response = await callPut(url, asset?.toJson());
      } else {
        response = await callPost(url, asset?.toJson());
      }
      return response?.data?.id;
    } catch (error) {
      !isLead && CognitoAuth.handleError(error, "saveAsset error");
    }
    return undefined;
  }

  async deleteAsset(
    assetId: number,
    estateId?: number
  ): Promise<number | undefined> {
    try {
      const url = `${Estate.path}/${estateId || "first"}/assets/${assetId}`;
      const response = await callDelete(url);
      return response?.data?.success || response?.data?.error;
    } catch (error) {
      !isLead && CognitoAuth.handleError(error, "deleteAsset error");
    }
    return undefined;
  }

  async saveAssetImage(
    file?: File,
    assetId?: number
  ): Promise<AssetImageUploadResult | null> {
    if (file && assetId) {
      try {
        const formData = new FormData();
        formData.append("image", file, file.name);
        formData.append("asset_id", assetId.toString());
        const response = await callPostWithFormData(
          `${laravelApiV1}${AssetImage.path}`,
          formData
        );
        return response?.data as AssetImageUploadResult;
      } catch (error) {
        !isLead && CognitoAuth.handleError(error, "saveAssetImage error");
        return null;
      }
    }
    return null;
  }

  async getProbateType(estateId: number): Promise<ProbateType | undefined> {
    try {
      const response = await callGet(
        `${Estate.path}/${estateId}/probate-type`
      );
      if (response?.data) {
        const probateType = ProbateType.fromJSON(response?.data);
        return probateType;
      } else {
        throw Error("Estate Form addition failed");
      }
    } catch (error) {
      !isLead && CognitoAuth.handleError(error, "getProbateType error");
    }
    return undefined;
  }
}
